import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Select, MenuItem, FormControl, InputLabel, Alert, CircularProgress,
  IconButton, Tooltip, Paper,
} from '@mui/material';
import { Add, CheckCircle, Delete, Edit } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import millingApi from '../../../services/millingApi';

const statusColor: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
  completed: 'success', rejected: 'error', in_progress: 'info', pending: 'default',
};

const DegermingList: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await millingApi.getDegermingBatches(statusFilter ? { status: statusFilter } : {});
      setRows(res.data ?? res);
    } catch { setError('Failed to load batches'); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleComplete = async (id: string) => {
    if (!window.confirm('Mark batch as completed?')) return;
    try { await millingApi.completeDegerming(id); load(); }
    catch { setError('Complete failed'); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this batch?')) return;
    try { await millingApi.deleteDegermingBatch(id); load(); }
    catch { setError('Delete failed'); }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>Maize Degerming</Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/milling/degerming/new')}>
          New Batch
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Batch #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Grain Lot</TableCell>
                <TableCell align="right">Input (MT)</TableCell>
                <TableCell align="right">Endosperm (MT)</TableCell>
                <TableCell align="right">Endosperm Yield %</TableCell>
                <TableCell align="right">Germ (MT)</TableCell>
                <TableCell align="right">Germ Yield %</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Operator</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={11} align="center">No batches found</TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.batchNumber}</TableCell>
                  <TableCell>{r.processDate}</TableCell>
                  <TableCell>{r.grainLotNumber || '-'}</TableCell>
                  <TableCell align="right">{r.inputQtyMt ?? '-'}</TableCell>
                  <TableCell align="right">{r.degermedEndospermQty ?? '-'}</TableCell>
                  <TableCell align="right">
                    {r.endospermYieldPct != null ? (
                      <Typography variant="body2" sx={{ color: r.endospermYieldPct >= 75 ? 'success.main' : 'warning.main', fontWeight: 'bold' }}>
                        {r.endospermYieldPct}%
                      </Typography>
                    ) : '-'}
                  </TableCell>
                  <TableCell align="right">{r.germQty ?? '-'}</TableCell>
                  <TableCell align="right">{r.germYieldPct != null ? `${r.germYieldPct}%` : '-'}</TableCell>
                  <TableCell>
                    <Chip label={r.status?.replace('_', ' ')} size="small" color={statusColor[r.status] ?? 'default'} />
                  </TableCell>
                  <TableCell>{r.operatorName || '-'}</TableCell>
                  <TableCell>
                    {r.status !== 'completed' && r.status !== 'rejected' && (
                      <Tooltip title="Complete">
                        <IconButton size="small" color="success" onClick={() => handleComplete(r.id)}>
                          <CheckCircle fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => navigate(`/milling/degerming/${r.id}/edit`)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default DegermingList;
